import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";

import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import DrugScheduleService from "../../service/DrugScheduleService";
import PatientProgressNotesService from "../../service/PatientProgressNotesService";
import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import {
  DS_V2_ADD, DS_V2_UPDATE, DS_V2_REFRESH, DS_V2_CLOSE, DS_V2_EDIT_CAPTURED,
  DS_V2_SHEET_COUNT,
} from "./events";
import {
  STATUS, normaliseStatus, formatDateShort, addDaysISO, drugNameOf,
  filterDrugs,
} from "./helpers";
import { noteAuthor } from "../../helper/progressNoteFormatters";

/**
 * Current logged-in user's display identity — { name, id }.
 *
 * The id comes from `noteAuthor`, the one derivation every progress-note author
 * block uses, so a dose note and a hand-written note by the same person carry
 * the SAME actor. This used to have its own chain with `profile.id` in the
 * middle, which on a doctor session would have stamped the platform auth id —
 * a different namespace from the doctor id the rest of the feature writes, and
 * invisible until two notes by one person failed to match each other.
 *
 * The "care manager" name fallback stays local: it is also what `createdby`
 * carries on the drug-schedule rows themselves, which is not a progress-note
 * author and should not go out blank.
 */
const currentUser = () => {
  try {
    const auth = new AuthenticationService();
    const me = noteAuthor(auth.getUserProfile());
    return { name: me.name || "care manager", id: me.employeeId };
  } catch (e) {
    return { name: "care manager", id: 0 };
  }
};

const currentUserName = () => currentUser().name;

/**
 * Fire a progress-note POST after a successful drug-schedule save. Two
 * shapes:
 *
 *   • "schedule-created" — { drugName, patientId }
 *     → "<p><DRUG_NAME> drug schedule successfully created</p>\n"
 *   • "dose-taken"       — { drugName, patientId, patientFullName,
 *                            doseNumber, actualDateISO }
 *     → "<p><PATIENT_FULL_NAME> has taken <DRUG_NAME> dose number <N>
 *        on <DD MMM YYYY></p>\n"
 *
 * Fire-and-forget: the drug-schedule save has already succeeded by the
 * time this runs, so any failure here is logged and swallowed — a
 * progress-note outage should never surface as a drug-schedule error to
 * the user. Callers pass a bare object; missing / empty required fields
 * short-circuit before the network call.
 */
const fireProgressNote = ({ kind, drugName, patient, doseNumber, actualDateISO }) => {
  try {
    const drug = String(drugName || "").trim();
    if (!drug) return;
    const patientId = patient && (patient.id || patient.patientID);
    if (!patientId) return;
    const author = currentUser();
    let body;
    if (kind === "created") {
      body = `<p>${drug} drug schedule successfully created</p>\n`;
    } else if (kind === "dose-taken") {
      const fullName = [patient.firstName, patient.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || patient.name || "Patient";
      const dateLabel = formatDateShort(actualDateISO);
      // formatDateShort returns "—" (em-dash) as its bad-input sentinel;
      // don't ship "…on —" to the note.
      if (!dateLabel || dateLabel === "—") return;
      body = `<p>${fullName} has taken ${drug} dose number ${doseNumber} on ${dateLabel}</p>\n`;
    } else {
      return;
    }
    const svc = new PatientProgressNotesService();
    svc.createProgressNote(
      {
        acknowledgers: [],
        author: { name: author.name, employeeId: author.id },
        body,
        comments: [],
        patientId,
        tag: 2,
      },
      // eslint-disable-next-line no-console
      () => {},
      (err) => {
        // eslint-disable-next-line no-console
        console.warn("[drug-schedule] progress-note POST failed:", err && err.message);
      },
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[drug-schedule] progress-note dispatch threw:", e && e.message);
  }
};

/**
 * Drug schedule editor rail — the right column when a user opens Add
 * or Update. One sheet at a time; only one plan can be worked on.
 *
 * Mode is driven by the payload of the open event:
 *   DS_V2_ADD    → AddSheet
 *   DS_V2_UPDATE → UpdateSheet (Active plans only)
 *
 * The View flow is NOT handled by this rail — it opens as a centered
 * modal owned by DrugSchedule.js (see DrugScheduleView.js). Doing it
 * that way keeps read-only browsing decoupled from the editor rail's
 * open/close semantics and lets the user view a plan without disturbing
 * whatever else is in the rail.
 *
 * The drug catalog is fetched once per session on first open — small
 * (~15 entries in prod, unlikely to hit >200) so client-side fuzzy
 * filter is fine.
 */

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M6 18L18 6" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16M9 7V4h6v3M6 7l1 12h10l1-12" />
  </svg>
);

/* Figma draws these four as Material icon-font GLYPHS (`lock` 14x17,
   `flag` 11x13, `search` 16x16, `md-cheveron-down` 16x16). This module
   has no icon font, and the emoji they were standing in for cannot be
   recoloured by CSS — so they are redrawn as stroked SVGs, matching the
   idiom of TrashIcon / CloseIcon above and inheriting `currentColor`. */

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
);

// Fitted viewBox, not 0 0 24 24: the glyph is TALLER than it is wide
// (11x13 in Figma) and a square box would letterbox it off-centre.
const FlagIcon = () => (
  <svg viewBox="0 0 12 14" width="11" height="13" fill="none"
    stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.2 13.2V1.4M2.2 1.4h7.6L8 4.6l1.8 3.2H2.2" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none"
    stroke="currentColor" strokeWidth="1.6"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="7" cy="7" r="4.5" />
    <path d="M10.4 10.4L14 14" />
  </svg>
);

const PickerCaret = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none"
    stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6.5l4 4 4-4" />
  </svg>
);

const CalendarGlyph = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
    stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M3.5 10h17" />
  </svg>
);

// ── Shared building blocks ─────────────────────────────────────────

const SheetHeader = ({ title, subtitle, onClose, right }) => (
  <header className="ds-sheet-head">
    <div className="ds-sheet-titles">
      <h3 className="ds-sheet-title">{title}</h3>
      {subtitle ? <p className="ds-sheet-sub">{subtitle}</p> : null}
    </div>
    <div className="ds-sheet-head-actions">
      {right}
      <button type="button" className="ds-sheet-close" onClick={onClose} aria-label="Close">
        <CloseIcon />
      </button>
    </div>
  </header>
);
SheetHeader.propTypes = {
  title:    PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  onClose:  PropTypes.func.isRequired,
  right:    PropTypes.node,
};

/** Read-only HTML render of the drug's criticalinstruction. Sanitised
 *  via dompurify (which is already a phr-frontend dep). Returns null
 *  when the text is empty, so the caller can skip the whole panel. */
const InstructionPanel = ({ html }) => {
  const text = String(html || "").trim();
  if (!text) return null;
  const clean = DOMPurify.sanitize(text, { USE_PROFILES: { html: true } });
  return (
    <div className="ds-instr">
      <div className="ds-instr-head">
        <span className="ds-instr-label">Instruction for planner</span>
      </div>
      <div
        className="ds-instr-body"
        // Content is sanitised above; dangerouslySetInnerHTML is
        // the only way to render the drug's <b> tags in prod data.
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </div>
  );
};
InstructionPanel.propTypes = { html: PropTypes.string };

/**
 * Status chip picker — drop-in replacement for the plain <select> the
 * Update sheet's header used to carry. Trigger renders in the same
 * `.ds-chip is-{state}` colour palette the middle-column STATUS
 * column already uses (orange / green / red) so the sheet's status
 * pill matches the table's row-status chip 1:1.
 *
 * Terminal-state locking (Completed / Expired can't be re-transitioned)
 * happens at the CALLER: `DrugSchedule.js` only routes Active plans
 * into the Update sheet, so this picker doesn't need a per-option
 * disabled rule — every option is a legal move from Active.
 */
const ChipCaret = () => (
  <svg viewBox="0 0 10 6" width="10" height="6" fill="none"
    stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 1l4 4 4-4" />
  </svg>
);
const chipClassFor = (s) => (
  s === "Active"    ? "ds-chip is-active"    :
  s === "Completed" ? "ds-chip is-completed" :
  s === "Expired"   ? "ds-chip is-expired"   :
                      "ds-chip"
);
const StatusChipPicker = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  // Outside-click / Escape close so the menu behaves like a native
  // select — no lingering popover if the reviewer moves on.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const options = ["Active", "Completed", "Expired"];
  return (
    <div className="ds-statuschip-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`${chipClassFor(value)} ds-chip-btn ds-statuschip-btn`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ds-chip-dot" />
        {value}
        <ChipCaret />
      </button>
      {open && (
        <ul className="ds-statuschip-menu" role="listbox">
          {options.map((opt) => (
            <li key={opt} role="option" aria-selected={opt === value}>
              <button
                type="button"
                className={`${chipClassFor(opt)} ds-chip-btn ds-statuschip-opt`}
                onClick={() => { onChange(opt); setOpen(false); }}
              >
                <span className="ds-chip-dot" />
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
StatusChipPicker.propTypes = {
  value:    PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// ── Add sheet ──────────────────────────────────────────────────────

const AddSheet = ({
  patient,
  drugs,
  drugsLoading,
  activeDrugNames,
  // Sheet-stack props (from useSheetStack via SheetStack children):
  formId,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // Transcript-mode extension. When present, the sheet does NOT hit
  // pharmacy-dose-schedule-api on Save — it hands the body up to the
  // AiTranscriptModal's CapturedSections, which stashes it in
  // /captured so Review Save & next commits it later (matches CC/Dx).
  //   onManualSave(body, { onSuccess, onError }) => void
  onManualSave,
  // Transcript-mode extension. When present + the reviewer has picked
  // a drug, the Reject button proposes the drafted plan then flips it
  // to rejected server-side so the audit row surfaces with
  // strikethrough — rather than silently dropping the sheet. Mirrors
  // ComplaintForm's onReject(formId, payload) flow.
  //   onManualReject(body, { onSuccess, onError }) => void
  onManualReject,
  // Reopen-a-staged-plan mode. When present, seeds the picker + dates
  // + doses + gaps from the captured payload so the reviewer can
  // modify a not-yet-committed plan without duplicating it.
  initialFromCaptured,
  // Paired with initialFromCaptured — Save routes through this instead
  // of onManualSave so the same captured item is updated in place.
  //   onCapturedUpdate(capturedId, body, { onSuccess, onError }) => void
  onCapturedUpdate,
  // Transcript-mode revert on top-right X close. Only fires on
  // reopened staged cards (`initialFromCaptured` set); server resets
  // Current ← LastCommitted so an uncommitted AI delta or in-progress
  // reviewer edit is dropped and the row reverts to the last-committed
  // snapshot. SAFE semantic — vs the footer Reject which is
  // DESTRUCTIVE (flips state=rejected + strikethrough). Mirrors the
  // CC/Dx .zy-card-x contract.
  //   onCapturedRevert(capturedId) => void
  onCapturedRevert,
  // Notifies the parent editor whenever the drug picker resolves. Lets
  // the parent block a second open sheet from picking the same drug
  // (the "already added" set for other sheets picks this up).
  //   onDrugPick(formId, drugname | "") => void
  onDrugPick,
  // "is-ai" | "is-manual" | "is-update" — extra class on the sheet
  // root + Save button so the CSS can paint AI-originated cards with
  // the maroon theme reviewers use to spot pre-filled proposals.
  themeClass,
}) => {
  const patientId = patient && (patient.id || patient.patientID);
  const service = useRef(new DrugScheduleService()).current;

  // Compute one-shot seeds from a staged captured plan (only used when
  // the sheet is reopened via DS_V2_EDIT_CAPTURED). Everything else
  // starts blank as before.
  const seed = useMemo(() => {
    if (!initialFromCaptured) return null;
    const doses = Array.isArray(initialFromCaptured.doses)
      ? initialFromCaptured.doses
      : [];
    const first = doses[0] || {};
    const drugName = String(first.dosename || drugNameOf(initialFromCaptured) || "").trim();
    return {
      drugName,
      startDate: String(first.planneddate || "").slice(0, 10),
      doseCount: doses.length ? String(doses.length) : "",
      // gapDays derived from each dose's nextdosefrequency.value; the
      // final row's gap is empty. Coerce to number safely.
      rows: doses.map((d, i) => ({
        plannedDate: String(d.planneddate || "").slice(0, 10),
        gapDays: i < doses.length - 1
          ? Math.max(0, parseInt((d.nextdosefrequency && d.nextdosefrequency.value) || 0, 10) || 0)
          : 0,
      })),
    };
    // initialFromCaptured is a fresh object per sheet open; recomputing
    // on identity change is fine — the sheet lifecycle owns the state.
  }, [initialFromCaptured]);

  // Drug picker
  const [query, setQuery] = useState(() => (seed ? seed.drugName : ""));
  const [picked, setPicked] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const filtered = useMemo(() => filterDrugs(drugs, query), [drugs, query]);
  // The picker menu is a floating list, so a click landing anywhere
  // else on the page (including inside the same sheet) must dismiss
  // it — otherwise it stays open behind the start-date field and eats
  // the reviewer's next click. Esc closes it too, matching the
  // catalog-picker UX everywhere else in the app.
  const pickerRef = useRef(null);
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDocPointer = (e) => {
      const root = pickerRef.current;
      if (root && !root.contains(e.target)) setPickerOpen(false);
    };
    const onDocKey = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    document.addEventListener("keydown", onDocKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      document.removeEventListener("keydown", onDocKey);
    };
  }, [pickerOpen]);

  // Start date + doses (count)
  const [startDate, setStartDate] = useState(() => (seed ? seed.startDate : ""));
  const [doseCount, setDoseCount] = useState(() => (seed ? seed.doseCount : ""));

  /** Dose plan rows. Each row holds:
   *    plannedDate — locked to startDate + sum(prior gaps)
   *    gapDays     — "next dose in N days"; empty on the final row
   *  Adding/removing rows or editing gaps rebuilds subsequent planned
   *  dates. Recompute happens in `recomputeDates`. */
  const [rows, setRows] = useState(() => (seed ? seed.rows : []));

  // Confirmation modal + save state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Seeded from captured payload but the catalog may load later — as
  // soon as it arrives, look up the matching drug so `picked` reflects
  // the seeded name and the rest of the form unlocks. Runs at most
  // once per sheet lifetime. If no catalog row matches (AI emitted a
  // name outside the whitelist, or a casing/spacing drift), synthesise
  // a `picked` from the captured name alone — the drug input is
  // readOnly for the life of a seeded sheet so identity is fixed, and
  // canSave only needs `picked` non-null to progress. Critical
  // instruction stays blank (catalog pharmacy copy isn't recoverable
  // from the captured payload); banner just doesn't render.
  useEffect(() => {
    if (!seed || picked) return;
    if (!Array.isArray(drugs) || drugs.length === 0) return;
    const target = seed.drugName.toLowerCase();
    const match = drugs.find((d) => String(d.drugname || "").toLowerCase() === target);
    if (match) {
      setPicked(match);
    } else if (seed.drugName) {
      setPicked({ drugname: seed.drugName, criticalinstruction: "" });
    }
  }, [seed, picked, drugs]);

  // Report the current pick up to the parent editor so it can build
  // per-sheet "already added" sets that exclude drugs already picked
  // in other open sheets. Clears on unmount so a re-opened sheet
  // doesn't inherit a ghost entry.
  useEffect(() => {
    if (typeof onDrugPick !== "function") return undefined;
    onDrugPick(formId, picked ? picked.drugname : "");
    return () => { onDrugPick(formId, ""); };
    // Fire on every picked change; onDrugPick is stable via useCallback
    // in the parent so it doesn't churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, formId]);

  // Regenerate rows whenever startDate or doseCount changes. Preserves
  // any user-entered gap values by index; if the row count grows, new
  // rows start with empty gaps.
  useEffect(() => {
    const n = Math.max(0, parseInt(doseCount, 10) || 0);
    if (n === 0) { setRows([]); return; }
    setRows((prev) => {
      const next = [];
      for (let i = 0; i < n; i += 1) {
        next.push({
          gapDays:     i < n - 1 ? (prev[i] && prev[i].gapDays) || "" : "",
        });
      }
      return next;
    });
  }, [doseCount]);

  // Recompute planned dates from startDate + cumulative gaps.
  const rowsWithDates = useMemo(() => {
    if (!startDate || rows.length === 0) return rows.map(() => ({ plannedDate: "" }));
    const out = [];
    let cur = startDate;
    for (let i = 0; i < rows.length; i += 1) {
      out.push({
        plannedDate: cur,
        gapDays:     rows[i].gapDays,
      });
      const gap = parseInt(rows[i].gapDays, 10);
      cur = Number.isFinite(gap) && gap > 0 ? addDaysISO(cur, gap) : "";
    }
    return out;
  }, [rows, startDate]);

  // The instruction is drug-catalog data (never persisted on the plan).
  const criticalHtml = picked ? picked.criticalinstruction : "";

  // Every non-final row must have a positive integer gap. Final row's
  // gap is intentionally blank (it's the last dose).
  const allGapsValid = rows.length > 0 && rows.every((r, i) => {
    if (i === rows.length - 1) return true;
    const g = parseInt(r.gapDays, 10);
    return Number.isFinite(g) && g > 0;
  });
  // Any date is allowed for backfilling except pre-1900 — that's almost
  // certainly a typo (misplaced year) and would push every dose-plan
  // computation off by a century. Matches the `min` on the input below;
  // duplicated here because some browsers don't strictly enforce the
  // attribute on paste.
  const isStartDateInRange = !startDate || Number(startDate.slice(0, 4)) >= 1900;
  const canSave = !!picked && !!startDate && isStartDateInRange && rows.length > 0 && allGapsValid && !!patientId;

  // Report "form has content" up to the sheet stack so the blank-guard
  // knows whether another "+ Add" click should refuse. Any single
  // field being non-empty counts as filled; a fresh sheet with nothing
  // typed reads as blank.
  useEffect(() => {
    if (typeof onFilledChange !== "function") return;
    const filled = !!(picked || query.trim() || startDate || doseCount);
    onFilledChange(formId, filled);
  }, [formId, onFilledChange, picked, query, startDate, doseCount]);

  const updateGap = (idx, val) => {
    // Keep only digits so the spinner stays numeric even after paste.
    const clean = String(val).replace(/\D/g, "").slice(0, 3);
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, gapDays: clean } : r)));
  };

  const addDose = () => {
    setRows((prev) => [...prev, { gapDays: "" }]);
    setDoseCount((prev) => String((parseInt(prev, 10) || 0) + 1));
  };

  const removeLastDose = () => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.slice(0, -1));
    setDoseCount((prev) => String(Math.max(1, (parseInt(prev, 10) || 1) - 1)));
  };

  // Body matches the exact shape prod entries carry (verified from
  // {"pid":895542,"status":"Completed","doses":[...]}). Shared by the
  // Save and Reject paths so both route through the same adapter.
  const buildBody = () => ({
    pid:    patientId,
    status: STATUS.ACTIVE,
    doses:  rowsWithDates.map((r, i) => ({
      doseno:            i + 1,
      dosename:          picked ? picked.drugname : "",
      planneddate:       r.plannedDate,
      rescheduledate:    "",
      actualdate:        "",
      // Backend expects value as a STRING per the prod shape.
      nextdosefrequency: {
        unit:  "days",
        value: i < rows.length - 1 ? String(r.gapDays) : "",
      },
    })),
    // Snapshot the logged-in employee's name — this is what shows
    // up in the "Created by" column on the main list.
    createdby: currentUserName(),
  });

  const submit = () => {
    if (!canSave || blockedBySibling) return;
    setSaving(true);
    setSaveError("");
    if (typeof onSavingChange === "function") onSavingChange(formId, true);
    const body = buildBody();
    // Transcript mode — edit-captured branch: update the SAME captured
    // item in place instead of proposing a new one. The initial
    // captured wrapper carries the id we need. Falls through to the
    // ordinary add-captured path if the parent didn't wire this
    // handler (defensive).
    if (initialFromCaptured && typeof onCapturedUpdate === "function") {
      const captured = initialFromCaptured._captured;
      const capturedId = captured && captured.id;
      if (capturedId) {
        onCapturedUpdate(capturedId, body, {
          onSuccess: () => {
            setSaving(false);
            setConfirmOpen(false);
            if (typeof onSavingChange === "function") onSavingChange(formId, false);
            onClose(formId);
          },
          onError: (err) => {
            setSaving(false);
            if (typeof onSavingChange === "function") onSavingChange(formId, false);
            setSaveError((err && err.message) || "Save failed. Try again.");
          },
        });
        return;
      }
    }
    // Transcript mode: stash to /captured instead of hitting the
    // pharmacy service. CapturedSections handles the propose+decide
    // dance and closes the sheet on success. No DS_V2_REFRESH here —
    // the persisted section list is only refreshed once Review Save
    // & next actually writes the plan to pharmacy.
    if (typeof onManualSave === "function") {
      onManualSave(body, {
        onSuccess: () => {
          setSaving(false);
          setConfirmOpen(false);
          if (typeof onSavingChange === "function") onSavingChange(formId, false);
          onClose(formId);
        },
        onError: (err) => {
          setSaving(false);
          if (typeof onSavingChange === "function") onSavingChange(formId, false);
          setSaveError((err && err.message) || "Save failed. Try again.");
        },
      });
      return;
    }
    service.create(
      body,
      () => {
        setSaving(false);
        setConfirmOpen(false);
        if (typeof onSavingChange === "function") onSavingChange(formId, false);
        EventService.emit(DS_V2_REFRESH);
        // Fire-and-forget progress note — the schedule save has already
        // landed; if the note POST fails the reviewer sees no error.
        fireProgressNote({
          kind:     "created",
          drugName: picked.drugname,
          patient,
        });
        // Dismiss this sheet — sheet-stack removes it from the rail.
        onClose(formId);
      },
      (err) => {
        setSaving(false);
        if (typeof onSavingChange === "function") onSavingChange(formId, false);
        setSaveError((err && err.message) || "Save failed. Try again.");
      },
    );
  };

  // Transcript-mode label. Either the "add captured" or "edit
  // captured" branch counts — both defer save to the transcript
  // pipeline instead of hitting pharmacy directly.
  const isTranscript =
    typeof onManualSave === "function" || !!initialFromCaptured;
  return (
    <div className={`ds-sheet${themeClass ? ` ${themeClass}` : ""}`}>
      {/* ZY-10356 Phase 6 — top-right X close only on REOPENS of a
          staged captured (`initialFromCaptured` set). First-opens
          (+ Add schedule or an AI first-emit that hasn't been committed
          yet) leave the footer Reject as the sole dismiss affordance.
          Fires the SAFE revert path (server resets Current ← LC) when
          wired; otherwise falls through to the local sheet-stack close.
          Mirrors CC / Dx / Addictions / PA / Anthropometric. */}
      {isTranscript && initialFromCaptured ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={() => {
            const capId = initialFromCaptured
              && initialFromCaptured._captured
              && initialFromCaptured._captured.id;
            if (capId && typeof onCapturedRevert === "function") {
              onCapturedRevert(capId);
            }
            onClose(formId);
          }}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      {/* Header (title + close icon) intentionally omitted per the locked
          mock — reviewer lands directly on the Drug picker. Cancel is
          still reachable from the footer action row. */}
      <div className="ds-sheet-body">
        {/* Drug picker */}
        <div className="ds-field">
          <label className="ds-field-label">
            Drug<span className="cc3-req">*</span>{" "}
            <span className="ds-field-hint">· fuzzy search</span>
          </label>
          <div className="ds-picker" ref={pickerRef}>
            <div className="ds-picker-field">
              <span className="ds-picker-icon"><SearchIcon /></span>
              <input
                type="text"
                className="ds-input ds-picker-input"
                value={picked ? picked.drugname : query}
                placeholder={drugsLoading ? "Loading catalog…" : "Search a drug…"}
                onChange={(e) => {
                  setQuery(e.target.value); setPicked(null); setPickerOpen(true);
                }}
                // Click / mousedown is the explicit "I want to pick"
                // gesture; programmatic focus (mount-focus helper,
                // tab-in from a sibling field) intentionally does NOT
                // open the menu — otherwise every fresh sheet would
                // land with the dropdown already expanded on top of
                // the rest of the form. Second click on a picked
                // field clears the lock so the input becomes
                // editable and the menu can render.
                //
                // ZY-10387: the input is NO LONGER locked on reopen of
                // a staged captured. The reviewer needs to be able to
                // correct a mis-captured drug (AI emitted the wrong
                // whitelist entry; speaker meant a different drug) —
                // same editability every other section's reopened card
                // offers for its main field.
                onMouseDown={() => {
                  if (picked) {
                    setPicked(null);
                    setQuery("");
                  }
                  setPickerOpen(true);
                }}
                disabled={drugsLoading}
              />
              {/* Indicator only -- pointer-events:none in the SCSS, so the
                  click still lands on the input and opens the menu the
                  same way it always did. Hidden in edit-captured mode
                  since the picker can't be reopened there. */}
              {!initialFromCaptured && (
                <span className={`ds-picker-caret${pickerOpen && !picked ? " is-open" : ""}`}>
                  <PickerCaret />
                </span>
              )}
            </div>
            {pickerOpen && !picked && !initialFromCaptured ? (
              <div className="ds-picker-menu">
                {filtered.length === 0 ? (
                  <div className="ds-picker-empty">No matching drugs</div>
                ) : (
                  filtered.slice(0, 50).map((d) => {
                    // Drug already on an ACTIVE schedule — greyed out
                    // with an "already added" pill; click is a silent
                    // no-op so the reviewer opens the row from the
                    // Content table's Update flow instead. Mirrors the
                    // Physical activity catalog's `is-already-active`
                    // treatment.
                    const already = activeDrugNames.has(
                      String(d.drugname || "").trim().toLowerCase()
                    );
                    return (
                      <button
                        key={String(d.id)}
                        type="button"
                        className={`ds-picker-opt${already ? " is-already-active" : ""}`}
                        aria-disabled={already}
                        title={
                          already
                            ? "Already on an active schedule — update it from the list instead"
                            : undefined
                        }
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (already) return;
                          setPicked(d);
                          setPickerOpen(false);
                          setQuery("");
                        }}
                      >
                        <span className="ds-picker-opt-name">{d.drugname}</span>
                        {already && (
                          <span className="ds-picker-opt-tag" aria-hidden="true">
                            already added
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Start date + doses count */}
        <div className="ds-field-row">
          <label className="ds-field">
            <span className="ds-field-label">
              Start date<span className="cc3-req">*</span>
            </span>
            <input
              type="date"
              className="ds-input"
              value={startDate}
              onChange={(e) => {
                const v = e.target.value;
                // Reject pre-1900 typed years outright — the `min`
                // attribute greys out earlier dates in the picker, but
                // a manually-typed year like "0055" slips past. Keep
                // the prior value so the field never holds an invalid
                // date. isStartDateInRange stays as a belt-and-braces
                // Save-time guard for anything that somehow does land.
                if (v && Number(v.slice(0, 4)) < 1900) return;
                setStartDate(v);
              }}
              disabled={!picked}
              min="1900-01-01"
            />
            {startDate && !isStartDateInRange ? (
              <span className="ds-field-err">Start date must be 1 Jan 1900 or later.</span>
            ) : null}
          </label>
          <label className="ds-field ds-field-narrow">
            <span className="ds-field-label">
              Doses<span className="cc3-req">*</span>
            </span>
            <input
              type="number"
              min="1"
              max="100"
              className="ds-input"
              value={doseCount}
              onChange={(e) => setDoseCount(e.target.value.replace(/\D/g, "").slice(0, 3))}
              disabled={!picked || !startDate}
            />
          </label>
        </div>

        {/* Dose plan */}
        <div className="ds-field">
          <span className="ds-field-label">
            Dose plan<span className="cc3-req">*</span>
          </span>
          {rows.length === 0 ? (
            <div className="ds-plan-empty">
              <span className="ds-plan-empty-icon" aria-hidden="true"><CalendarGlyph /></span>
              <p className="ds-plan-empty-line">
                Pick a drug, start date &amp; number of doses<br />
                — the plan will generate here
              </p>
            </div>
          ) : (
            <ul className="ds-plan-list">
              {rowsWithDates.map((r, i) => (
                <li className="ds-plan-row" key={i}>
                  <div className="ds-plan-left">
                    <span className="ds-plan-doseno">
                      DOSE {i + 1}
                      {i > 0 ? <span className="ds-plan-auto">auto</span> : null}
                    </span>
                    <span className="ds-plan-date">
                      <CalendarGlyph /> {formatDateShort(r.plannedDate)}
                    </span>
                  </div>
                  {i < rowsWithDates.length - 1 ? (
                    <div className="ds-plan-right is-gap">
                      <span className="ds-plan-gaplabel">NEXT DOSE IN</span>
                      <div className="ds-plan-gapfield">
                        <input
                          type="number"
                          min="1"
                          max="365"
                          className="ds-input ds-gapinput"
                          value={r.gapDays}
                          onChange={(e) => updateGap(i, e.target.value)}
                          placeholder="00"
                        />
                        <span className="ds-plan-gapunit">days</span>
                      </div>
                    </div>
                  ) : (
                    <div className="ds-plan-right is-final">
                      <span className="ds-plan-final"><FlagIcon />FINAL DOSE</span>
                      <button type="button" className="ds-icon-btn" onClick={removeLastDose} title="Remove this dose">
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </li>
              ))}
              <li className="ds-plan-adddose">
                <button type="button" className="ds-ghost-btn ds-plan-addbtn" onClick={addDose}>
                  + Add dose
                </button>
              </li>
            </ul>
          )}
        </div>

        {/* Instruction (read-only HTML, snapshotted from drug catalog) */}
        <InstructionPanel html={criticalHtml} />

        {/* Save-lock notice — hidden in transcript mode. The dose-date
            lock takes effect only when the plan actually lands in the
            pharmacy DB (Review Save & next), so showing it in-call is
            misleading; the same message appears on the Review Save
            screen where the lock genuinely applies. */}
        {!isTranscript && (
          <div className="ds-lock-note">
            <span className="ds-lock-icon"><LockIcon /></span>
            Once saved, dose dates are locked — only the status can change.
          </div>
        )}

        {saveError ? <p className="ds-err">{saveError}</p> : null}
      </div>

      <footer className="ds-sheet-foot">
        <button
          type="button"
          className="ds-ghost-btn"
          onClick={() => {
            // ZY-10312 — transcript-mode Reject with content: when the
            // reviewer has picked a drug, propose + reject so the audit
            // row surfaces with strikethrough (CC rejectManualCard
            // parity). Without a picked drug there's nothing worth
            // auditing, so fall through to the plain local close.
            if (isTranscript && typeof onManualReject === "function" && picked && !saving) {
              setSaving(true);
              setSaveError("");
              if (typeof onSavingChange === "function") onSavingChange(formId, true);
              onManualReject(buildBody(), {
                onSuccess: () => {
                  setSaving(false);
                  if (typeof onSavingChange === "function") onSavingChange(formId, false);
                  onClose(formId);
                },
                onError: (err) => {
                  setSaving(false);
                  if (typeof onSavingChange === "function") onSavingChange(formId, false);
                  setSaveError((err && err.message) || "Reject failed. Try again.");
                },
              });
              return;
            }
            onClose(formId);
          }}
          disabled={saving}
        >{isTranscript ? "Reject" : "Cancel"}</button>
        <button
          type="button"
          className={`ds-cta${themeClass ? ` ${themeClass}` : ""}`}
          // Transcript-mode adds are TEMPORARY (they land in /captured
          // until Review Save & next writes to pharmacy), so the
          // "dose dates are locked" confirmation is misleading — the
          // reviewer can still edit before commit. Skip the modal and
          // go straight to submit. Standalone mode keeps the confirm
          // since that DOES lock immediately.
          onClick={isTranscript ? submit : () => setConfirmOpen(true)}
          disabled={!canSave || saving || blockedBySibling}
          title={blockedBySibling ? "Another schedule is saving — hang on a moment." : undefined}
        >
          {saving
            ? (isTranscript ? "Adding…" : "Saving…")
            : (isTranscript ? "Add" : "Save schedule")}
        </button>
      </footer>

      {confirmOpen ? (
        <ConfirmDialog
          title="Save drug schedule?"
          body="Once confirmed, dose dates can't be changed. Only the status (Active → Completed / Expired) can be updated later."
          keepLabel="Keep editing"
          confirmLabel={saving ? "Saving…" : "Confirm & save"}
          onKeep={() => setConfirmOpen(false)}
          onConfirm={submit}
          confirming={saving}
        />
      ) : null}
    </div>
  );
};
AddSheet.propTypes = {
  patient:          PropTypes.object,
  drugs:            PropTypes.array.isRequired,
  drugsLoading:     PropTypes.bool,
  /** Set<lowercased drugname> — drugs currently on an ACTIVE plan.
   *  The picker greys those options and blocks the click. */
  activeDrugNames:  PropTypes.instanceOf(Set),
  // Sheet-stack contract:
  formId:           PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  blockedBySibling: PropTypes.bool,
  onFilledChange:   PropTypes.func,
  onSavingChange:   PropTypes.func,
  onClose:          PropTypes.func.isRequired,
  // Transcript-mode extension. Optional — absent on the standalone
  // patient page, present when mounted inside AiTranscriptModal.
  onManualSave:     PropTypes.func,
  onManualReject:   PropTypes.func,
  // Transcript-mode edit-captured extension — the same AddSheet reopened
  // from a staged captured plan. Seed comes from the plan payload;
  // Save routes through onCapturedUpdate.
  initialFromCaptured: PropTypes.object,
  onCapturedUpdate:    PropTypes.func,
  onCapturedRevert:    PropTypes.func,
  // Cross-sheet drug-lock reporter (see DrugScheduleEditor).
  onDrugPick:          PropTypes.func,
  themeClass:       PropTypes.string,
};
AddSheet.defaultProps = {
  activeDrugNames:  new Set(),
  blockedBySibling: false,
  onFilledChange:   () => {},
  onSavingChange:   () => {},
  onManualSave:     null,
  onManualReject:   null,
  onCapturedRevert: null,
  themeClass:       "",
};

// ── Update sheet ───────────────────────────────────────────────────

const UpdateSheet = ({
  patient,
  drugsById,
  // Sheet-stack contract — sheet.editing IS the plan.
  editing,
  formId,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
}) => {
  // Alias for readability — the rest of the sheet was written against a
  // `plan` local when this was a singleton.
  const plan = editing;
  const service = useRef(new DrugScheduleService()).current;

  const [status, setStatus] = useState(normaliseStatus(plan.status) || STATUS.ACTIVE);
  const [actuals, setActuals] = useState(() =>
    (plan.doses || []).map((d) => d.actualdate || ""),
  );
  // Snapshot of each dose's actualdate at mount time. Used on save to
  // detect which rows GAINED an actual — those are the doses the CM
  // just marked "taken", and each one earns its own progress note.
  const initialActuals = useRef((plan.doses || []).map((d) => d.actualdate || "")).current;
  const [expireOpen, setExpireOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Sequential editing rules (locked with user 2026-08-26):
  //   * A dose that already has an actualdate (Taken) is frozen — the
  //     input is disabled so the date can't be rewritten.
  //   * Among the un-taken doses, only the FIRST one is editable. This
  //     enforces recording adherence in dose order — user can't skip
  //     ahead and mark dose 3 taken while doses 1 & 2 are still open.
  //   * The editable dose's min date is the previous dose's actualdate
  //     (or the plan start date if this is the very first dose). This
  //     prevents an out-of-sequence date entry.
  const doses = plan.doses || [];
  const nextEditableIndex = useMemo(() => {
    for (let i = 0; i < doses.length; i += 1) {
      if (!String(doses[i].actualdate || "").trim()) return i;
    }
    return -1; // every dose already has an actualdate
  }, [doses]);
  const startDate = doses[0] ? doses[0].planneddate : "";
  const minDateForDose = (i) => {
    // Dose 1's floor is the plan's start date; subsequent doses'
    // floor is the previous dose's ACTUAL date (or empty if the
    // previous dose is un-taken — shouldn't happen given sequential
    // rule but we handle it defensively).
    if (i === 0) return startDate || "";
    const prevActual = String(doses[i - 1].actualdate || "").trim();
    return prevActual || "";
  };

  // Would every dose be Taken AFTER the current in-flight edits land?
  // This is what drives the auto-flip-to-Completed rule on save: when
  // the care manager fills the final missing actual and hits Save,
  // the schedule closes itself as Completed — no need to also toggle
  // the status dropdown.
  const allTakenIncludingEdits = doses.length > 0
    && doses.every((_, i) => String(actuals[i] || "").trim());

  const updateActual = (idx, val) => {
    setActuals((prev) => prev.map((v, i) => (i === idx ? val : v)));
  };

  // Look up the drug's criticalinstruction by matching drugname —
  // instruction isn't persisted on the plan (deliberate: latest
  // guidance wins). Empty if the drug was pruned from the catalog.
  const drug = drugsById[drugNameOf(plan).toLowerCase()];

  // An update sheet is intrinsically "filled" (it's editing an existing
  // plan), so the sheet stack should treat it as such — a "+ Add" click
  // never buries an update sheet.
  useEffect(() => {
    if (typeof onFilledChange === "function") onFilledChange(formId, true);
  }, [formId, onFilledChange]);

  const submit = (nextStatus) => {
    if (blockedBySibling) return;
    setSaving(true);
    setErr("");
    if (typeof onSavingChange === "function") onSavingChange(formId, true);
    // Auto-flip to Completed when the pending write would fill every
    // dose's actualdate — spares the care manager an extra step. The
    // rule is UX-side (we agreed no backend change on the pivot);
    // pharmacy-dose-schedule-api just persists whatever status we send.
    // The only status this override applies to is Active → Completed;
    // an explicit Expired transition still goes through as Expired.
    let effective = nextStatus;
    if (nextStatus === STATUS.ACTIVE && allTakenIncludingEdits) {
      effective = STATUS.COMPLETED;
    }
    const body = {
      id:     plan.id || plan._id,
      pid:    plan.pid,
      status: effective,
      doses:  (plan.doses || []).map((d, i) => ({
        ...d,
        actualdate: actuals[i] || "",
      })),
      createdby: plan.createdby || "care manager",
    };
    service.update(
      body,
      () => {
        setSaving(false);
        setExpireOpen(false);
        setCompleteOpen(false);
        if (typeof onSavingChange === "function") onSavingChange(formId, false);
        EventService.emit(DS_V2_REFRESH);
        // Fire one progress note per dose that just gained an actual
        // date — i.e. rows whose baseline `initialActuals[i]` was empty
        // but the current `actuals[i]` is set. Fire-and-forget; a note
        // failure never blocks the schedule update.
        const drugLabel = drugNameOf(plan);
        (plan.doses || []).forEach((d, i) => {
          const before = String(initialActuals[i] || "").trim();
          const now    = String(actuals[i] || "").trim();
          if (!before && now) {
            fireProgressNote({
              kind:           "dose-taken",
              drugName:       drugLabel,
              patient,
              doseNumber:     d.doseno || i + 1,
              actualDateISO:  now,
            });
          }
        });
        // Dismiss this sheet — sheet-stack removes it from the rail.
        onClose(formId);
      },
      (e) => {
        setSaving(false);
        if (typeof onSavingChange === "function") onSavingChange(formId, false);
        setErr((e && e.message) || "Update failed. Try again.");
      },
    );
  };

  const onSaveClick = () => {
    // Completed / Expired have no per-dose gate — reviewer can close a
    // course out early (all-doses-taken is no longer required). The
    // confirm dialog still fires so an accidental transition into a
    // terminal state gets a second-chance prompt.
    if (status === STATUS.EXPIRED)   { setExpireOpen(true);   return; }
    if (status === STATUS.COMPLETED) { setCompleteOpen(true); return; }
    // Status = Active on the dropdown, but submit() will auto-flip to
    // Completed when `allTakenIncludingEdits` is true. See submit() above.
    submit(STATUS.ACTIVE);
  };

  return (
    <div className="ds-sheet">
      <SheetHeader
        title={drugNameOf(plan) || "Schedule"}
        subtitle={`Created ${formatDateShort((plan.createdAt || "").slice(0, 10))} · by ${plan.createdby || "—"}`}
        onClose={() => onClose(formId)}
        right={
          <StatusChipPicker
            value={status}
            onChange={setStatus}
            disabled={saving}
          />
        }
      />
      <div className="ds-sheet-body">
        <ul className="ds-upd-list">
          {doses.map((d, i) => {
            const taken = !!String(d.actualdate || "").trim();
            // Only the FIRST un-taken dose is editable; Taken doses
            // are frozen; un-taken doses further down are disabled
            // until each preceding one is filled in order.
            const editable = !saving
              && status === STATUS.ACTIVE
              && i === nextEditableIndex
              && !taken;
            const min = editable ? minDateForDose(i) : undefined;
            return (
              <li className={`ds-upd-row${taken ? " is-taken" : ""}${editable ? " is-editable" : ""}`} key={i}>
                <div className="ds-upd-left">
                  <span className="ds-plan-doseno">DOSE {d.doseno || i + 1}</span>
                  <span className="ds-plan-date">
                    {formatDateShort(d.planneddate)}
                  </span>
                </div>
                <div className="ds-upd-right">
                  <span className="ds-upd-label">
                    ACTUAL DOSE DATE<span className="cc3-req">*</span>
                  </span>
                  {taken ? (
                    // Frozen row — show a green pill with the recorded
                    // date + check mark, matching the mockup. No input,
                    // no way to type over it.
                    <span className="ds-upd-taken-pill">
                      <svg viewBox="0 0 16 16" width="12" height="12" fill="none"
                        stroke="currentColor" strokeWidth="2.2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="8" cy="8" r="7" />
                        <path d="M5 8l2 2 4-4" />
                      </svg>
                      {formatDateShort(d.actualdate)}
                    </span>
                  ) : (
                    <input
                      type="date"
                      className="ds-input ds-upd-input"
                      value={actuals[i] || ""}
                      onChange={(e) => updateActual(i, e.target.value)}
                      disabled={!editable}
                      min={min || undefined}
                      title={
                        !editable
                          ? "Fill earlier doses first"
                          : min
                            ? `Must be on or after ${formatDateShort(min)}`
                            : undefined
                      }
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {drug ? <InstructionPanel html={drug.criticalinstruction} /> : null}

        {err ? <p className="ds-err">{err}</p> : null}
      </div>

      <footer className="ds-sheet-foot">
        {/* UpdateSheet is standalone-only (pharmacy backend; no
            transcript-mode entrypoint). Always "Cancel". */}
        <button type="button" className="ds-ghost-btn" onClick={() => onClose(formId)} disabled={saving}>Cancel</button>
        <button
          type="button"
          className="ds-cta"
          onClick={onSaveClick}
          disabled={saving || blockedBySibling}
          title={blockedBySibling ? "Another schedule is saving — hang on a moment." : undefined}
        >
          {saving ? "Saving…" : "Update schedule"}
        </button>
      </footer>

      {expireOpen ? (
        <ConfirmDialog
          title="Mark schedule as Expired?"
          body={`${drugNameOf(plan) || "This schedule"} · Active → Expired. Remaining doses will no longer be tracked. This can't be undone.`}
          keepLabel="Keep editing"
          confirmLabel={saving ? "Updating…" : "Confirm update"}
          confirmDanger
          onKeep={() => setExpireOpen(false)}
          onConfirm={() => submit(STATUS.EXPIRED)}
          confirming={saving}
        />
      ) : null}

      {completeOpen ? (
        <ConfirmDialog
          title="Mark schedule as Completed?"
          body={`${drugNameOf(plan) || "This schedule"} · Active → Completed. The schedule will be closed and can't be re-opened. Start a new schedule if the patient needs another course.`}
          keepLabel="Keep editing"
          confirmLabel={saving ? "Updating…" : "Confirm update"}
          onKeep={() => setCompleteOpen(false)}
          onConfirm={() => submit(STATUS.COMPLETED)}
          confirming={saving}
        />
      ) : null}
    </div>
  );
};
UpdateSheet.propTypes = {
  patient:          PropTypes.object,
  drugsById:        PropTypes.object.isRequired,
  // Sheet-stack contract:
  editing:          PropTypes.object.isRequired,   // the plan being edited
  formId:           PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  blockedBySibling: PropTypes.bool,
  onFilledChange:   PropTypes.func,
  onSavingChange:   PropTypes.func,
  onClose:          PropTypes.func.isRequired,
};
UpdateSheet.defaultProps = {
  blockedBySibling: false,
  onFilledChange:   () => {},
  onSavingChange:   () => {},
};

// ── Confirm dialog (small, reusable) ───────────────────────────────

const ConfirmDialog = ({ title, body, keepLabel, confirmLabel, confirmDanger, onKeep, onConfirm, confirming }) => (
  <div className="ds-confirm-backdrop" role="presentation" onClick={onKeep}>
    <div className="ds-confirm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
      <header className="ds-confirm-head">
        <h4 className="ds-confirm-title">{title}</h4>
        <button type="button" className="ds-sheet-close" onClick={onKeep} aria-label="Close">
          <CloseIcon />
        </button>
      </header>
      <p className="ds-confirm-body">{body}</p>
      <footer className="ds-confirm-foot">
        <button type="button" className="ds-ghost-btn" onClick={onKeep} disabled={confirming}>
          {keepLabel}
        </button>
        <button
          type="button"
          className={confirmDanger ? "ds-cta ds-cta-danger" : "ds-cta"}
          onClick={onConfirm}
          disabled={confirming}
        >
          {confirmLabel}
        </button>
      </footer>
    </div>
  </div>
);
ConfirmDialog.propTypes = {
  title:         PropTypes.string.isRequired,
  body:          PropTypes.string.isRequired,
  keepLabel:     PropTypes.string.isRequired,
  confirmLabel:  PropTypes.string.isRequired,
  confirmDanger: PropTypes.bool,
  onKeep:        PropTypes.func.isRequired,
  onConfirm:     PropTypes.func.isRequired,
  confirming:    PropTypes.bool,
};

// ── Editor rail (sheet stack) ──────────────────────────────────────
//
// Multi-sheet rail: each "+ Add schedule" pushes a fresh add sheet on
// top; each Update chip click pushes an update sheet on top. The shared
// useSheetStack + SheetStack helpers give us:
//   • blank-sheet guard — a second "+ Add" click on an untouched blank
//     add sheet pulses that sheet instead of stacking a second blank.
//   • dup guard — clicking Update on a plan already open surfaces the
//     existing sheet instead of racing two writes.
//   • serialised saves + save-lock while any sibling is writing.
//   • refusal pulse + a11y live region.
// Same pattern CC / Dx / Med / Hosp / Allergy / FH rails already use.

const ADD_EVENTS = [DS_V2_ADD];
// DS_V2_EDIT_CAPTURED shares the edit channel: its payload is the same
// {plan, patient} shape (with plan._isStaged + plan._captured set), and
// useSheetStack dedupes on the same getRecordId(plan) key. The
// SheetStack renderer branches on plan._isStaged to swap UpdateSheet
// out for a pre-filled AddSheet (see below).
const EDIT_EVENTS = [DS_V2_UPDATE, DS_V2_EDIT_CAPTURED];

// Payload shape on DS_V2_UPDATE is {plan, patient} — the reduce-to-record
// helper for useSheetStack pulls `plan` out. dupMessage speaks the
// drug name so the refusal pulse text is unambiguous.
const getRecord = (payload) => payload && payload.plan;
const getRecordId = (plan) => plan && plan.id;
const dupMessage = (plan) => `${drugNameOf(plan) || "This schedule"} is already open for update.`;

const DrugScheduleEditor = ({
  patient,
  onManualSave,
  onManualReject,
  onCapturedUpdate,
  onCapturedRevert,
  stagedDrugNames,
  addThemeClass,
  // ZY-10356 — the transcript modal reuses this editor as its own
  // right rail for Drug schedule (there's no modal-local DS_V2_ADD
  // listener; the modal piggybacks on this component). Set true on
  // the modal mount so useSheetStack's transcript-modal gate keeps
  // this instance active while the shell mount (same prop defaulted
  // to false) goes silent.
  isModalMount,
}) => {
  const patientId = patient && (patient.id || patient.patientID);
  const service = useRef(new DrugScheduleService()).current;

  const stack = useSheetStack({
    sectionKey: "drug-schedule",
    addEvents: ADD_EVENTS,
    editEvents: EDIT_EVENTS,
    getRecord,
    getRecordId,
    blankMessage: "A blank drug schedule form is already open.",
    dupMessage,
    enabled: !!patientId,
    isModalMount,
  });

  // Per-open-sheet picked drug — {formId: drugname}. Each AddSheet
  // fires onDrugPick when its picker resolves, so the SheetStack
  // renderer can subtract the CURRENT sheet's pick and add every
  // OTHER open sheet's pick to that sheet's "already added" set. Two
  // open sheets can no longer race the same drug into the picker.
  const [openSheetDrugs, setOpenSheetDrugs] = useState({});
  const handleDrugPick = useCallback((formId, name) => {
    setOpenSheetDrugs((prev) => {
      const key = String(formId);
      if (!name) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === name) return prev;
      return { ...prev, [key]: name };
    });
  }, []);
  // Prune formIds whose sheet has been closed so a subsequent open of
  // the same drug (in a fresh sheet) isn't wrongly blocked by a stale
  // ghost entry.
  useEffect(() => {
    setOpenSheetDrugs((prev) => {
      const liveIds = new Set(stack.sheets.map((s) => String(s.id)));
      const keys = Object.keys(prev);
      const staleKeys = keys.filter((k) => !liveIds.has(k));
      if (staleKeys.length === 0) return prev;
      const next = { ...prev };
      staleKeys.forEach((k) => delete next[k]);
      return next;
    });
  }, [stack.sheets]);

  // Drug catalog, cached at rail-mount time. Small enough that a single
  // fetch covers every subsequent open of the editor.
  const [drugs, setDrugs] = useState([]);
  const [drugsLoading, setDrugsLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setDrugsLoading(true);
    service.listDrugs(
      (res) => { if (alive) { setDrugs(res); setDrugsLoading(false); } },
      ()    => { if (alive) { setDrugs([]);  setDrugsLoading(false); } },
    );
    return () => { alive = false; };
  }, [service]);

  const drugsById = useMemo(() => {
    const m = {};
    (drugs || []).forEach((d) => { m[String(d.drugname || "").toLowerCase()] = d; });
    return m;
  }, [drugs]);

  // Active-plan drug names — used by AddSheet's picker to mark drugs
  // already on the patient's active schedule with an "already added"
  // pill (matches the Physical activity catalog UX). Refetched whenever
  // the section table refreshes; kept as a lowercase Set so the picker's
  // predicate is a cheap O(1) lookup.
  const [activeDrugNames, setActiveDrugNames] = useState(() => new Set());
  useEffect(() => {
    if (!patientId) { setActiveDrugNames(new Set()); return undefined; }
    let alive = true;
    const loadActive = () => {
      service.list(
        patientId,
        (res) => {
          if (!alive) return;
          const names = new Set();
          (res || []).forEach((plan) => {
            if (normaliseStatus(plan.status) !== STATUS.ACTIVE) return;
            const nm = drugNameOf(plan);
            if (nm) names.add(String(nm).trim().toLowerCase());
          });
          setActiveDrugNames(names);
        },
        () => { if (alive) setActiveDrugNames(new Set()); },
      );
    };
    loadActive();
    EventService.addListener(DS_V2_REFRESH, loadActive);
    return () => {
      alive = false;
      EventService.removeListener(DS_V2_REFRESH, loadActive);
    };
  }, [patientId, service]);

  // Emit DS_V2_CLOSE when the stack becomes empty so the transcript
  // modal's CapturedSections collapses the right rail. useSheetStack
  // already emits RAIL_OPEN / RAIL_CLOSE for the shell contract; this
  // is the drug-schedule-specific mirror for the transcript rail gate.
  const prevIsOpen = useRef(false);
  useEffect(() => {
    if (prevIsOpen.current && !stack.isOpen) {
      EventService.emit(DS_V2_CLOSE);
    }
    prevIsOpen.current = stack.isOpen;
  }, [stack.isOpen]);

  // Emit the live sheet count so the transcript modal can render the
  // tab's "N open" pill correctly when the reviewer stacks more than
  // one Add/Edit sheet at once — the boolean DS_V2_ADD/CLOSE pair only
  // tells you "at least one is open" and pegs the pill at 1.
  const sheetCount = stack.sheets.length;
  useEffect(() => {
    EventService.emit(DS_V2_SHEET_COUNT, { count: sheetCount });
  }, [sheetCount]);

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }
  // Idle: stack.isOpen === false → SheetStack returns null and the rail
  // has no visual presence. Matches the shell contract (shell emits
  // RAIL_CLOSE, table takes full width).
  return (
    <SheetStack
      stack={stack}
      className="cc3-editor"
      describe={(s) => (s.editing ? `Update ${drugNameOf(s.editing) || "schedule"}` : "Add schedule")}
    >
      {(sheet, props) => {
        // Effective "already-added active drugs" set for THIS sheet:
        //   server-loaded active plans
        //   ∪ transcript staged plans
        //   ∪ every OTHER open sheet's currently-picked drug
        //   ∖ this sheet's own current pick (so re-opening the picker
        //     doesn't grey out our own drug)
        //   ∖ the drug being edited when reopened from a staged plan
        //     (same reason)
        const merged = new Set(activeDrugNames);
        (stagedDrugNames || []).forEach((n) => merged.add(n));
        const myKey = String(sheet.id);
        Object.entries(openSheetDrugs).forEach(([id, name]) => {
          if (id !== myKey && name) merged.add(String(name).trim().toLowerCase());
        });
        const myPick = openSheetDrugs[myKey];
        if (myPick) merged.delete(String(myPick).trim().toLowerCase());
        const editingStaged = sheet.editing && sheet.editing._isStaged;
        if (editingStaged) {
          const cur = String(drugNameOf(sheet.editing) || "").trim().toLowerCase();
          if (cur) merged.delete(cur);
        }

        // Transcript-mode edit-captured: reuse AddSheet, pre-filled from
        // the staged captured payload. Save routes through
        // onCapturedUpdate (updates the captured item in place) instead
        // of onManualSave (which would create a new one).
        //
        // Theme:
        //   - captured.source === "ai" (ANY state) → is-ai (maroon) —
        //     an AI-authored card, regardless of whether the reviewer
        //     has committed yet. Fires for pending / added_in_call /
        //     edited_in_call so the reopen theme matches the first
        //     open. Previously this gated on state === "proposed" which
        //     NEVER matches the current state enum (pending / added /
        //     edited / rejected / reviewed) — so the maroon theme never
        //     fired and AI-captured sheets reverted to the manual
        //     indigo on every mount.
        //   - otherwise → is-manual (indigo) — reviewer re-editing
        //     a plan they staged themselves.
        if (editingStaged) {
          const captured = sheet.editing._captured;
          const stagedTheme = (captured && captured.source === "ai")
            ? "is-ai"
            : (addThemeClass || "is-manual");
          return (
            <AddSheet
              patient={patient}
              drugs={drugs}
              drugsLoading={drugsLoading}
              activeDrugNames={merged}
              initialFromCaptured={sheet.editing}
              onCapturedUpdate={onCapturedUpdate}
              // Thread onManualReject into the reopen branch too — the
              // Reject button's `typeof onManualReject === "function"`
              // gate was failing because this branch previously didn't
              // forward the prop (only the fresh-add branch below did).
              // Without it, Reject on a reopened staged card fell
              // through to a plain local close; the committed captured
              // stayed in added_in_call and the row never got the
              // strikethrough treatment. The underlying handler
              // (rejectManualCardDrug) merges via uniqueKey into the
              // existing captured and flips it to rejected — exactly
              // what the user expects.
              onManualReject={onManualReject}
              onCapturedRevert={onCapturedRevert}
              onDrugPick={handleDrugPick}
              themeClass={stagedTheme}
              {...props}
            />
          );
        }
        return sheet.editing ? (
          <UpdateSheet
            patient={patient}
            drugsById={drugsById}
            {...props}
          />
        ) : (
          <AddSheet
            patient={patient}
            drugs={drugs}
            drugsLoading={drugsLoading}
            activeDrugNames={merged}
            onManualSave={onManualSave}
            onManualReject={onManualReject}
            onDrugPick={handleDrugPick}
            themeClass={addThemeClass}
            {...props}
          />
        );
      }}
    </SheetStack>
  );
};
DrugScheduleEditor.propTypes = {
  patient:      PropTypes.object,
  // Optional; when provided, the AddSheet defers save to this handler
  // (transcript mode) instead of hitting pharmacy directly.
  onManualSave: PropTypes.func,
  // Optional; when provided, the AddSheet's Reject button proposes
  // the drafted plan + flips it to rejected server-side (so the audit
  // row surfaces with a strikethrough) instead of a plain local
  // sheet-drop. Mirrors CC's rejectManualCard flow.
  //   onManualReject(body, { onSuccess, onError }) => void
  onManualReject: PropTypes.func,
  // Optional; when provided, the AddSheet reopened from a staged
  // captured plan routes save through this handler
  //   (capturedId, body, { onSuccess, onError }) => void
  // so the captured item is updated in place. Passed by CapturedSections
  // in transcript mode; ignored in standalone.
  onCapturedUpdate: PropTypes.func,
  // Optional; wired by CapturedSections in transcript mode to the
  // server-side revertCaptured endpoint. Fired when the reviewer clicks
  // the top-right X on a REOPENED staged card — resets Current ← LC
  // so an uncommitted AI delta or in-progress edit is discarded. SAFE
  // path (vs the footer Reject which is DESTRUCTIVE).
  //   onCapturedRevert(capturedId) => void
  onCapturedRevert: PropTypes.func,
  // Optional; Set<lowercased drugname> of transcript-staged plans. In
  // transcript mode this is unioned with the server-loaded active
  // plans so the picker also blocks a same-drug second add via staged
  // rows. Undefined outside transcript.
  stagedDrugNames: PropTypes.instanceOf(Set),
  // Optional themeClass applied to every AddSheet mounted through this
  // editor (transcript mode passes "is-manual" for user-opened cards).
  // Kept configurable so a future "AI proposed" card flow can pass
  // "is-ai" to render the maroon variant.
  addThemeClass: PropTypes.string,
  // Transcript mode flag — see component body comment. Default false
  // keeps shell-mounted instances gated by useSheetStack's
  // transcript-modal check.
  isModalMount: PropTypes.bool,
};

export default DrugScheduleEditor;
